import { publicEnv } from '@/lib/config/public-env';
/**
 * Constant values, literal unions, and maps used throughout the application.
 * This provides a single source of truth for fixed values.
 *
 * @module @portal/types/constants
 */

/**
 * Document types supported in the analysis system.
 * Defines the various file types that can be processed.
 */
export const DOCUMENT_TYPE = {
    /** PDF document type */
    PDF: 'PDF',
    /** Excel spreadsheet type */
    EXCEL: 'EXCEL',
    /** Word document type */
    WORD: 'WORD',
    /** Powerpoint presentation type */
    PPTX: 'PPTX',
    /** Image file type */
    IMAGE: 'IMAGE',
    /** Video file type */
    VIDEO: 'VIDEO',
    /** Audio file type */
    AUDIO: 'AUDIO',
} as const;

/**
 * Type representing one of the available document types
 */
export type DocumentType = (typeof DOCUMENT_TYPE)[keyof typeof DOCUMENT_TYPE];

/**
 * Permission levels in the system.
 * Defines the various access levels users can have.
 */
export enum UserRole {
    INTERNAL = 'internal',
    CLIENT = 'client',
    ADMIN = 'admin',
    DEVELOPER = 'developer',
    CANARY = 'canary',
}

/**
 * API configuration constants
 */
export const API = {
    /** Base API URL from environment variable */
    URL: publicEnv('NEXT_PUBLIC_PORTAL_API_BASE_ENDPOINT'),
    /** GraphQL endpoint URL */
    GRAPHQL_URL: publicEnv('NEXT_PUBLIC_PORTAL_API_BASE_ENDPOINT')
        ? `${publicEnv('NEXT_PUBLIC_PORTAL_API_BASE_ENDPOINT')}/graphql`
        : undefined,
    /** Service name for the portal API */
    SERVICE_NAME: 'PortalService',
} as const;

/**
 * Local storage key for survey selection
 */
export const SURVEY_SELECTION_KEY = 'surveySelection';

// Stable empty array constants to avoid creating new references on each render
export const EMPTY_SURVEY_ARRAY: { id: string; label: string }[] = [];
export const EMPTY_QUESTION_IDS: string[] = [];
// Stable empty set for when no surveys are selected
export const EMPTY_SURVEY_SET: Set<string> = new Set();

/**
 * Analysis section tab configuration
 * Defines the available tabs in the analysis section
 */
export const ANALYZE_TABS = [
    { key: 'questions', title: 'Responses', href: 'field/questions' },
    { key: 'images', title: 'Images', href: 'analyze/images' },
    {
        key: 'videos',
        title: 'DANI Reel Studio',
        href: 'analyze/videos',
        icon: 'lucide:sparkles',
        isNew: true,
    },
    { key: 'insights', title: 'Insights', href: 'analyze/insights' },
    { key: 'documents', title: 'Documents', href: 'analyze/documents' },
] as const;

/**
 * Questions export options
 * Available export options for question data
 */
export const QUESTIONS_EXPORT_OPTIONS = [
    'All Question Data',
    'Filtered Question Data',
    'Summary Tables',
    // 'SPSS',
] as const;

/**
 * Multimedia export options
 * Available export options for multimedia data
 */
export const MULTIMEDIA_EXPORT_OPTIONS = [
    'All Multimedia Data',
    'Filtered Multimedia Data',
] as const;

/**
 * Document type options with icons and display titles
 */
export const DOCUMENT_TYPE_OPTIONS = [
    { key: 'PDF', title: 'PDF', icon: 'FilePdf' },
    { key: 'EXCEL', title: 'Excel', icon: 'FileSpreadsheet' },
    { key: 'WORD', title: 'Word', icon: 'FileDocument' },
    { key: 'PPTX', title: 'Powerpoint', icon: 'FileSlideshow' },
    { key: 'VIDEO', title: 'Video', icon: 'FileVideo' },
] as const;

/**
 * Type representing one of the available analyze tabs
 */
export type AnalyzeTab = (typeof ANALYZE_TABS)[number]['key'];

/**
 * Type representing one of the available question export options
 */
export type QuestionsExportOption = (typeof QUESTIONS_EXPORT_OPTIONS)[number];

/**
 * Type representing one of the available multimedia export options
 */
export type MultimediaExportOption = (typeof MULTIMEDIA_EXPORT_OPTIONS)[number];

/**
 * Type representing one of the available document type options
 */
export type DocumentTypeOption = (typeof DOCUMENT_TYPE_OPTIONS)[number];
